import { getFormErrorMessage } from "@core/utility/get-error-message";
import { LocationFormRule, LocationFormSchema } from "@factory/form/form.rule";
import { useFormDefaultLocation } from "@factory/form/form-default-location.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseLocationModel } from "@factory/model/query-model-location";
import { 
  useQyDeleteLocation, 
  useQyGetLocationById, 
  useQyUpdateLocation} from "@factory/query/location.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditLocation() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultLocation();
  const navigate = useNavigate();
  const { mapUpdateLocation, mapDeleteLocation } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { locationId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Location
  const handleGetApiSuccess = (data: GetResponseLocationModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
  };
  const {
    data: locationResponse,
    isLoading,
    isError: locationError,
  } = useQyGetLocationById(locationId || "", handleGetApiSuccess);
  const locationData = locationResponse?.data?.[0];

  // API UPDATE Location
  const handleUpdateApiSuccess = () => {
    showSuccess("Location updated successfully");
  };
  const { mutate: updateLocation, isLoading: isEditLoading } = useQyUpdateLocation(
    handleUpdateApiSuccess
  );

  // API DELETE Location
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Location deleted successfully");
  };
  const { mutate: deleteLocation } = useQyDeleteLocation(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<LocationFormSchema>({
    defaultValues: getDefault(locationData),
    resolver: zodResolver(LocationFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: LocationFormSchema) => {
    if (!locationData || !locationData.code) {
      throw new Error("No location data");
    }

    const formData = mapUpdateLocation(form, locationData.code);
    updateLocation(formData);
  };
  const handleValidateError = (err: FieldErrors<LocationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!locationData || !locationData.code) {
      throw new Error("No location data found");
    }

    const form = mapDeleteLocation(locationData.code);
    deleteLocation(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    locationId,
    locationData,
    isLoading,
    locationError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}